import React, { useEffect } from 'react';
import { get } from 'lodash';
import { Flex, Text } from '@chakra-ui/react';
import { RadioGroup } from '../RadioGroup.js';
import { useFormContext } from 'react-hook-form';
import { TextArea } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import { FormFieldDependentTextArea as IFormFieldDependentTextArea } from '@publishing-engine/domain/types/article.js';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks.js';

interface IFormFieldDependentTextAreaProps {
  config: IFormFieldDependentTextArea;
  isEditingAllowed?: boolean;
}

const FormFieldDependentTextArea: React.FC<
  IFormFieldDependentTextAreaProps
> = ({ config, isEditingAllowed = true }) => {
  const {
    formState: { errors },
    register,
    watch,
    control,
    setValue
  } = useFormContext();

  const {
    name,
    params: { labels, validations },
    caseDataFields: { main: field, dependsOn }
  } = config;

  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const optionKeys = Object.keys(labels.options);

  const options = optionKeys.map(key => {
    return {
      value: key === 'true' ? true : false,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="textBody">{labels.options[key]?.main}</Text>
          <Text textStyle="textBody">{labels.options[key]?.secondary}</Text>
        </Flex>
      )
    };
  });

  const error = get(errors, field);

  const exists = watch(dependsOnField);
  const fieldValue = watch(mainField);

  useEffect(() => {
    if (!exists) {
      setValue(mainField, '');
    }
  }, [exists]);

  return (
    <Flex flexDirection="column" gap={6}>
      <FieldLabel
        isRequired={validations.required}
        label={labels.main}
        subText={labels.subtitle}
        helpText={labels.helpText}
      />
      <Flex flexDirection="column">
        <RadioGroup
          name={dependsOnField}
          control={control}
          options={options}
          required={validations.required}
          gap={2}
          isEditingAllowed={isEditingAllowed}
        />
      </Flex>
      {exists ? (
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          gap={2}
          width="100%"
        >
          <FieldLabel
            label={labels.section.title}
            subText={parseStringWithLinks(labels.section.explanation)}
            isRequired
            helpText={labels.helpText}
          />
          <TextArea
            value={fieldValue}
            id={name}
            {...register(mainField, {
              required: validations.required,
              maxLength: validations.maxLength
            })}
            isInvalid={!!error}
            isReadOnly={!isEditingAllowed}
          />
        </Flex>
      ) : null}
      <FormFieldErrorMessage
        config={config}
        label={exists ? labels.section.title : labels.main}
      />
    </Flex>
  );
};

export default FormFieldDependentTextArea;
